import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 * @property {HTMLElement} toggleMediaSoundButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;
  isMuted = false;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
    
    // If media is already rendered (not in template), mark as loaded when it actually loads
    const existingMedia = this.querySelector('video, iframe[data-video-type]');
    if (existingMedia && !this.querySelector('template')) {
      if (existingMedia instanceof HTMLVideoElement || existingMedia instanceof HTMLIFrameElement) {
        this.#handlePreRenderedMedia(existingMedia);
      }
    }
  }
  
  /**
   * Handles media that was pre-rendered (not loaded from template)
   * @param {HTMLVideoElement | HTMLIFrameElement} media - The media element
   */
  #handlePreRenderedMedia(media) {
    const isAutoplay = this.hasAttribute('autoplay');
    
    if (media instanceof HTMLVideoElement) {
      // Initialize state
      this.isPlaying = !media.paused;
      this.isMuted = media.muted || media.volume === 0;
      this.updatePlayPauseHint(this.isPlaying);
      this.updateSound(this.isMuted);
      
      if (media.readyState >= 2) {
        // Media already loaded
        this.setAttribute('data-media-loaded', 'true');
        if (isAutoplay || this.isPlaying) {
          this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
        }
      } else {
        // Wait for media to load
        media.addEventListener('loadeddata', () => {
          this.setAttribute('data-media-loaded', 'true');
          if (isAutoplay || this.isPlaying) {
            this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
          }
        }, { once: true });
      }
      
      // Listen for play/pause events to update state
      media.addEventListener('play', () => {
        this.isPlaying = true;
        this.updatePlayPauseHint(this.isPlaying);
        this.setAttribute('data-media-loaded', 'true');
      });
      
      media.addEventListener('pause', () => {
        this.isPlaying = false;
        this.updatePlayPauseHint(this.isPlaying);
      });
      
      // Listen for volume changes
      media.addEventListener('volumechange', () => {
        this.isMuted = media.muted || media.volume === 0;
        this.updateSound(this.isMuted);
      });
      
    } else if (media instanceof HTMLIFrameElement) {
      // For iframes, assume muted by default for autoplay
      if (isAutoplay) {
        this.isMuted = true;
        this.updateSound(this.isMuted);
      }
      // For iframes, we can't reliably detect load, so set it immediately
      // The iframe will handle its own loading
      this.setAttribute('data-media-loaded', 'true');
      if (isAutoplay) {
        this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
        this.isPlaying = true;
        this.updatePlayPauseHint(this.isPlaying);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      // Show the toggle button when media is loaded
      if (this.getAttribute('data-media-loaded')) {
        toggleMediaButton.classList.remove('hidden');
      }
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Updates the visual hint for sound on/off state
   * @param {boolean} isMute - Whether the video is currently mute
   */
  updateSound(isMute) {
    const toggleSoundButton = this.refs.toggleMediaSoundButton;
    if (toggleSoundButton instanceof HTMLElement) {
      const muteIcon = toggleSoundButton.querySelector('.icon-mute');
      if (muteIcon) {
        // Hide mute icon when muted (show "SOUND OFF" when not muted)
        muteIcon.classList.toggle('hidden', isMute);
      }
      const unmuteIcon = toggleSoundButton.querySelector('.icon-unmute');
      if (unmuteIcon) {
        // Show unmute icon when muted (show "SOUND ON" when muted)
        unmuteIcon.classList.toggle('hidden', !isMute);
      }
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    // If media is already loaded (pre-rendered), just play it
    if (this.getAttribute('data-media-loaded')) {
      this.playMedia();
      return;
    }
    
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement) {
      // Initialize state
      this.isMuted = content.muted || content.volume === 0;
      this.updateSound(this.isMuted);
      
      // Set up event listeners for the newly loaded video
      content.addEventListener('play', () => {
        this.isPlaying = true;
        this.updatePlayPauseHint(this.isPlaying);
      });
      
      content.addEventListener('pause', () => {
        this.isPlaying = false;
        this.updatePlayPauseHint(this.isPlaying);
      });
      
      content.addEventListener('volumechange', () => {
        this.isMuted = content.muted || content.volume === 0;
        this.updateSound(this.isMuted);
      });
      
      if (content.getAttribute('autoplay')) {
        // force autoplay for safari
        content.play();
      }
    } else if (content instanceof HTMLIFrameElement) {
      // For iframes, assume muted by default for autoplay
      const isAutoplay = this.hasAttribute('autoplay');
      if (isAutoplay) {
        this.isMuted = true;
        this.updateSound(this.isMuted);
      }
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  toggleSound() {
    // Allow toggling sound even if not playing yet
    if (this.isMuted) {
      this.unmuteMedia();
    } else {
      this.muteMedia();
    }
  }

  /**
 * Mute the media
 */
  muteMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"mute","args":""}'
          : '{"method":"setVolume","value":0}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video) {
        video.muted = true;
        video.volume = 0;
      }
    }

    this.isMuted = true;
    this.updateSound(this.isMuted);
  }

  /**
   * Unmute the media
   */
  unmuteMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"unMute","args":""}'
          : '{"method":"setVolume","value":1}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video) {
        video.muted = false;
        video.volume = 1;
      }
    }

    this.isMuted = false;
    this.updateSound(this.isMuted);
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video) {
        video.play().catch(() => {
          // Handle play promise rejection (e.g., autoplay blocked)
        });
      }
    }
    this.isPlaying = true;
    // Ensure data-media-loaded is set when playing starts
    this.setAttribute('data-media-loaded', 'true');
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
